import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { isAuthError } from "@/lib/api";
import { getPublicSettings } from "@/lib/bootstrap";
import { useAuth } from "@/lib/AuthContext";
import { useAccounts, accountTypeKey } from "@/lib/AccountContext";
import { AccountSwitcher } from "@/components/AccountSwitcher";
import { CreateSubaccount } from "@/components/CreateSubaccount";
import { MoneyAmount } from "@/components/MoneyAmount";
import { usePdfViewer } from "@/lib/PdfViewerContext";
import { fmtMoney, fmtDateTime, groupIban } from "@/lib/format";
import { detectCategory } from "@/lib/iban";
import { authenticatedDownload } from "@/lib/download";
import { ReceiptPreview } from "@/components/DocumentPreview";
import TxDrawer from "@/components/TxDrawer";
import {
  ArrowUpRight, ArrowDownLeft, Download, Plus, Sparkles, Eye, EyeOff,
  Send, ShieldCheck, BadgeCheck, FileBadge2, TrendingUp, MessageCircle, Copy,
} from "lucide-react";
import BalanceSparkline from "@/components/BalanceSparkline";
import { toast } from "sonner";

function openSupportChat() {
  try {
    window.Tawk_API?.showWidget?.();
    if (window.Tawk_API?.maximize) window.Tawk_API.maximize();
    else if (window.Tawk_API?.toggle) window.Tawk_API.toggle();
    else setTimeout(() => { window.Tawk_API?.showWidget?.(); window.Tawk_API?.maximize?.(); }, 600);
  } catch {}
}

function QuickAction({ to, icon: I, label, accent, testId }) {
  return (
    <Link to={to}
      data-testid={testId}
      className="group rounded-2xl border border-slate-200 bg-white p-4 hover:border-blue-300 hover:shadow-[0_8px_30px_rgba(37,99,235,0.08)] transition-all flex flex-col items-start gap-3"
    >
      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: accent + "20", color: accent }}>
        <I size={18} />
      </div>
      <span className="text-sm font-semibold text-slate-900">{label}</span>
    </Link>
  );
}

export default function Overview() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { selectedAccount } = useAccounts();
  const { openPdf } = usePdfViewer();
  const isAdmin = user?.role === "admin";
  const acc = selectedAccount;
  const [txs, setTxs] = useState([]);
  const [txsLoading, setTxsLoading] = useState(true);
  const [siteSettings, setSiteSettings] = useState(null);
  const [hideBalance, setHideBalance] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    getPublicSettings().then((s) => setSiteSettings(s)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!acc?.id) { setTxs([]); return; }
    let alive = true;
    setTxsLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/transactions", { params: { limit: 8, account_id: acc.id } });
        if (alive) setTxs(data);
      } catch (e) {
        if (!isAuthError(e)) toast.error(t("overview.load_error"));
      } finally {
        if (alive) setTxsLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [acc?.id]);

  // A transfer affects the available balance as soon as it is created (the funds
  // are HELD on a pending transfer). Only rejected/refunded transfers (funds
  // returned) and not-yet-executed scheduled transfers are excluded from totals.
  const affectsBalance = (tx) => !["scheduled", "rejected", "refunded"].includes(tx.status);
  const settled = txs.filter(affectsBalance);
  const last30Credits = settled.filter((t) => t.amount > 0).reduce((s, t) => s + t.amount, 0);
  const last30Debits  = Math.abs(settled.filter((t) => t.amount < 0).reduce((s, t) => s + t.amount, 0));

  return (
    <div className="space-y-6 max-w-7xl" data-testid="overview-page">
      <div>
        <div className="font-mono-fin text-xs uppercase tracking-widest text-blue-600">{t("overview.hello")}</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl tracking-tighter text-slate-900 mt-1">
          {user?.full_name?.split(" ")[0]} 👋
        </h1>
      </div>

      <div className="flex flex-wrap gap-2 items-stretch">
        <AccountSwitcher />
        {!isAdmin && <CreateSubaccount />}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Bank card */}
        <div
          className="lg:col-span-2 relative rounded-3xl overflow-hidden p-4 sm:p-5 text-white glow-purple"
          style={{
            background: "linear-gradient(135deg, #0B0F2F 0%, #312E81 30%, #6D28D9 65%, #2563EB 100%)",
          }}
          data-testid="balance-card"
        >
          {/* decorative shapes */}
          <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full border border-white/10"></div>
          <div className="absolute right-6 -bottom-6 w-28 h-28 rounded-full border border-white/15"></div>
          <div className="absolute -right-2 top-20 w-24 h-24 rounded-full bg-purple-400/30 blur-3xl"></div>
          <div className="absolute left-1/3 -bottom-10 w-32 h-32 rounded-full bg-blue-400/30 blur-3xl"></div>

          <div className="relative">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono-fin text-[11px] uppercase tracking-widest text-purple-200">{t("overview.balance")}</div>
                <div className="flex items-baseline gap-2 sm:gap-3 mt-1">
                  <div className="font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tighter leading-none">
                    {acc === null ? (
                      <span className="inline-block w-40 sm:w-52 h-9 sm:h-10 rounded-xl bg-white/10 animate-pulse"></span>
                    ) : hideBalance ? "•• •••,••" : (
                      <MoneyAmount value={acc.balance} currency={acc.currency}
                        decimalClassName="text-xl sm:text-2xl md:text-3xl text-purple-200 align-baseline" />
                    )}
                  </div>
                  <button onClick={() => setHideBalance(!hideBalance)} className="text-purple-200 hover:text-white shrink-0" data-testid="toggle-balance">
                    {hideBalance ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                </div>
                <div className="text-xs text-purple-200 mt-1 truncate">{acc?.currency || "EUR"} · {acc?.label || t(accountTypeKey(acc?.account_type || "checking"))}</div>
              </div>
              <div className="font-display font-bold text-base sm:text-lg tracking-tight text-purple-100 shrink-0">
                Open<span className="text-white">Payments</span>
              </div>
            </div>

            <div className="mt-5 sm:mt-7 flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3 sm:gap-6">
              {!isAdmin && (
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-mono-fin uppercase tracking-widest text-purple-200">{t("overview.account_number")}</div>
                  {acc?.iban ? (
                    <div className="mt-1.5 space-y-1.5" data-testid="account-number-iban">
                      <div className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-white/10 border border-white/15 backdrop-blur max-w-full">
                        <span className="text-[13px] sm:text-sm font-mono-fin tracking-wide text-white truncate" data-testid="account-iban-value">{groupIban(acc.iban)}</span>
                        <button
                          onClick={() => { navigator.clipboard?.writeText(acc.iban).then(() => toast.success(t("overview.iban_copied"))).catch(() => {}); }}
                          className="text-purple-200 hover:text-white shrink-0" title={t("overview.copy_iban")} data-testid="copy-iban-btn"
                        >
                          <Copy size={13} />
                        </button>
                      </div>
                      {acc?.bic && (
                        <div className="text-[11px] text-purple-200">
                          BIC/SWIFT: <span className="font-mono-fin text-white/90">{acc.bic}</span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="mt-1.5 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-xl bg-white/10 border border-white/15 backdrop-blur" data-testid="account-number-info">
                        <MessageCircle size={14} className="text-cyan-300 shrink-0" />
                        <span className="text-[13px] sm:text-sm text-white/90">{t("overview.account_number_chat")}</span>
                      </div>
                      <button onClick={openSupportChat} className="mt-2 block text-xs text-cyan-300 hover:text-white font-semibold" data-testid="overview-open-chat">
                        {t("overview.open_chat")} →
                      </button>
                    </>
                  )}
                </div>
              )}
              <div className="shrink-0">
                <div className="text-[10px] font-mono-fin uppercase tracking-widest text-purple-200">{t("overview.holder")}</div>
                <div className="text-white text-sm mt-1 truncate">{acc?.holder_name}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Mini stats */}
        <div className="grid grid-cols-1 gap-5">
          <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="stat-income">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">{t("overview.income_recent")}</div>
              <span className="w-9 h-9 rounded-full bg-green-100 text-green-700 flex items-center justify-center"><TrendingUp size={16} /></span>
            </div>
            <div className="font-display text-3xl text-green-600 font-bold mt-2">
              {(acc === null || txsLoading)
                ? <span className="inline-block w-32 h-8 rounded-lg bg-green-100 animate-pulse align-middle"></span>
                : <>+<MoneyAmount value={last30Credits} currency={acc?.currency} decimalClassName="text-xl text-green-400" /></>}
            </div>
          </div>
          <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="stat-expense">
            <div className="flex items-center justify-between">
              <div className="text-[11px] font-mono-fin uppercase tracking-widest text-slate-500">{t("overview.expense_recent")}</div>
              <span className="w-9 h-9 rounded-full bg-red-100 text-red-700 flex items-center justify-center"><ArrowUpRight size={16} /></span>
            </div>
            <div className="font-display text-3xl text-red-600 font-bold mt-2">
              {(acc === null || txsLoading)
                ? <span className="inline-block w-32 h-8 rounded-lg bg-red-100 animate-pulse align-middle"></span>
                : <>-<MoneyAmount value={last30Debits} currency={acc?.currency} decimalClassName="text-xl text-red-400" /></>}
            </div>
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <QuickAction to="/app/transferencias" icon={Send}        label={t("overview.send_money")}     accent="#2563EB" testId="quick-send" />
        <QuickAction to="/app/movimientos"    icon={ArrowDownLeft} label={t("overview.movements")}    accent="#16A34A" testId="quick-movements" />
        <QuickAction to="/app/documentos"     icon={FileBadge2}  label={t("overview.my_certificates")} accent="#9333EA" testId="quick-documents" />
        <QuickAction to="/app/kyc"            icon={BadgeCheck}  label={t("overview.my_verification")} accent="#EA580C" testId="quick-kyc" />
      </div>

      {/* Balance evolution */}
      {txs.length > 0 && (
        <div className="rounded-3xl bg-white border border-slate-200 p-6" data-testid="balance-chart-card">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="font-mono-fin text-[10px] uppercase tracking-widest text-slate-500">{t("overview.balance_evolution_eyebrow")}</div>
              <h2 className="font-display font-bold text-lg text-slate-900 mt-0.5">{t("overview.balance_evolution_title")}</h2>
            </div>
            <span className="text-[10px] font-mono-fin text-slate-400">{t("overview.balance_evolution_hint")}</span>
          </div>
          <BalanceSparkline transactions={settled} currentBalance={acc?.balance || 0} days={30} height={130} currency={acc?.currency} />
        </div>
      )}

      {user?.kyc_status !== "verified" && (siteSettings?.kyc_banner_enabled ?? true) && (
        <div className="rounded-2xl border border-orange-300 bg-orange-50 p-5 flex flex-col md:flex-row items-start md:items-center justify-between gap-3" data-testid="kyc-banner">
          <div className="flex items-start gap-3">
            <ShieldCheck className="text-orange-600 mt-0.5" size={20} />
            <div>
              <div className="font-semibold text-orange-900" data-testid="kyc-banner-title">
                {siteSettings?.kyc_banner_title || t("overview.kyc_default_title")}
              </div>
              <div className="text-sm text-orange-800" data-testid="kyc-banner-message">
                {siteSettings?.kyc_banner_message || t("overview.kyc_default_msg")}
              </div>
            </div>
          </div>
          <Link to="/app/kyc" className="inline-flex items-center gap-2 bg-orange-900 text-white px-4 py-2 rounded-full text-sm font-semibold hover:bg-slate-900" data-testid="overview-kyc-cta">
            <Sparkles size={16} /> {siteSettings?.kyc_banner_cta_text || t("overview.kyc_default_cta")}
          </Link>
        </div>
      )}

      {/* Recent activity */}
      <div className="rounded-3xl bg-white border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-slate-900">{t("overview.recent_activity")}</h2>
          <Link to="/app/movimientos" className="text-sm text-blue-600 font-semibold hover:underline" data-testid="overview-view-all">
            {t("overview.view_all")} →
          </Link>
        </div>
        <div>
          {txs.length === 0 && <div className="px-6 py-10 text-center text-slate-500 text-sm">{t("overview.no_movements")}</div>}
          {txs.map((tx) => {
            const cat = detectCategory(tx.description);
            const filename = `comprobante_${(tx.reference || tx.id).replace(/[^A-Za-z0-9_-]/g, "_")}.pdf`;
            return (
              <div
                key={tx.id}
                onClick={() => setSelected(tx)}
                data-testid={`activity-row-${tx.id}`}
                className="px-6 py-3 border-b border-slate-50 last:border-0 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors group"
              >
                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0" style={{ background: cat.bg, color: cat.color }}>
                  {tx.amount > 0 ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-2 flex-wrap">
                    <span className="truncate">{tx.description}</span>
                    {tx.status === "pending" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-orange-100 text-orange-700" data-testid={`activity-status-${tx.id}`}>{t("movements.pending")}</span>
                    )}
                    {tx.status === "scheduled" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-blue-100 text-blue-700" data-testid={`activity-status-${tx.id}`}>{t("movements.scheduled")}</span>
                    )}
                    {tx.status === "rejected" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-red-100 text-red-700" data-testid={`activity-status-${tx.id}`}>{t("movements.rejected")}</span>
                    )}
                    {tx.status === "refunded" && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-mono-fin font-bold bg-purple-100 text-purple-700" data-testid={`activity-status-${tx.id}`}>{t("movements.refunded")}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: cat.bg, color: cat.color }}>{t(`tx.cat.${cat.key}`)}</span>
                    {tx.transfer_type && <span className="font-mono-fin">{t(`tx.type.${tx.transfer_type}`, { defaultValue: tx.transfer_type })}</span>}
                    <span>·</span>
                    <span>{fmtDateTime(tx.executed_at || tx.created_at)}</span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono-fin text-sm font-bold ${!affectsBalance(tx) ? "text-slate-400" : tx.amount > 0 ? "text-green-600" : "text-slate-900"}`}>
                    {tx.amount > 0 ? "+" : ""}{fmtMoney(tx.amount, tx.currency || acc?.currency)}
                  </div>
                  <div className="flex items-center gap-1 mt-1 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openPdf({
                          title: `${t("documents.receipt_doc_title")} ${tx.reference}`,
                          content: <ReceiptPreview tx={tx} account={acc} />,
                          path: `/documents/receipt/${tx.id}`,
                          filename,
                        });
                      }}
                      data-testid={`activity-view-${tx.id}`}
                      title={t("movements.view")}
                      className="p-1.5 rounded-full hover:bg-blue-50 text-slate-500 hover:text-blue-600"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); authenticatedDownload(`/documents/receipt/${tx.id}`, filename); }}
                      data-testid={`activity-download-${tx.id}`}
                      title={t("movements.pdf")}
                      className="p-1.5 rounded-full hover:bg-blue-50 text-slate-500 hover:text-blue-600"
                    >
                      <Download size={14} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <TxDrawer tx={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
